<script lang="ts">
    import {onMount} from 'svelte';
    import {guiCreatorDataStore} from './stores/guiCreatorData';
    import {UIElement} from './ui-elements/element';
    import {UIRectangle} from './ui-elements/rectangle/rectangle';
    import {TextAlignmentTypes, UIText} from './ui-elements/text/text';
    import {UIButton} from './ui-elements/button/button';
    import {HitBoxTypes, UITriangle} from './ui-elements/triangle/triangle';
    import {elementSelectionStore} from './stores/elementsSelection';
    import {loadConfig} from './services/storage.service';
    import {GUICreatorData} from './stores/types';
    import UIEditorNavBar from "./components/ui-editor/UIEditorPagesBar.svelte";
    import RightSideBar from "./components/right-sidebar/RightSideBar.svelte";
    import {inputFocusedStore} from './stores/inputFocus';
    import {getNewZValue, uiElementOf} from './utils/helpers';
    import {genId} from './utils/utils';
    import LeftSideBar from './components/left-sidbar/LeftSideBar.svelte';
    import {SCREEN_WIDTH} from './constants';
    import {decoupledGroupsStore} from './stores/decoupledGroups';
    import {UISlider} from './ui-elements/slider/slider';

    let selectedUiElements: UIElement[] = [];
    let copiedUiElements: UIElement[] = [];
    let currentPage = '';
    let availablePages: string[] = [];
    let canvasElement;
    let canvasCtx;
    let uiEditorElement;
    let pointerPosition = {x: 0, y: 0};
    let mouseDragging = false;
    let origin = {
        x: 0,
        y: 0,
    }
    let isUiInputFocused = false;
    let uiElementsList: UIElement[] = [];
    let showTopWidgetBar = false;

    function onShowTopWidgetBarChange(newValue) {
        showTopWidgetBar = newValue;
        renderUIEditor();
    }

    function resizeEditor() {
        canvasCtx = canvasElement.getContext('2d');
        canvasCtx.canvas.height = uiEditorElement.clientHeight;
        canvasCtx.canvas.width = uiEditorElement.clientWidth;
        const offset = SCREEN_WIDTH / 2;
        origin = {
            x: Math.trunc(canvasElement.clientWidth / 2 - offset),
            y: Math.trunc(canvasElement.clientHeight / 2 - offset),
        }
        renderUIEditor();
    }

    addEventListener("resize", () => resizeEditor());
    onMount(() => {
        loadCachedData();
        resizeEditor();

        guiCreatorDataStore.subscribe((storeGuiCreatorData: GUICreatorData) => {
            if (currentPage !== storeGuiCreatorData.currentPage) {
                elementSelectionStore.set([]);
            }
            currentPage = storeGuiCreatorData.currentPage;
            uiElementsList = guiCreatorDataStore.getRenderOrder(currentPage);
            const newSelectedList = uiElementsList.filter(uiElement => elementSelectionStore.isSelected(uiElement));
            elementSelectionStore.set(newSelectedList);
            availablePages = Object.keys(storeGuiCreatorData.pages.elements || {});
            renderUIEditor();
        });
        elementSelectionStore.subscribe((newSelectedUiElements: UIElement[]) => {
            selectedUiElements = newSelectedUiElements;
            renderUIEditor();
        });
        inputFocusedStore.subscribe((newInputFocusState: boolean) => {
            isUiInputFocused = newInputFocusState;
        })
    });


    function renderUIEditor() {
        canvasCtx.clearRect(0, 0, canvasElement.clientWidth, canvasElement.clientHeight);
        drawBangleScreenIndicator();
        if (showTopWidgetBar) {
            drawTopWidgetLine();
        }
        uiElementsList.forEach(uiElement => uiElement.renderElement(canvasCtx, origin));
    }

    function drawBangleScreenIndicator() {
        const borderWidth = 3;
        const offset = SCREEN_WIDTH / 2;
        const topLeft = {
            x: uiEditorElement.clientWidth / 2 - offset - borderWidth / 2,
            y: uiEditorElement.clientHeight / 2 - offset - borderWidth / 2,
        }
        const path = new Path2D();
        path.moveTo(topLeft.x, topLeft.y);
        path.lineTo(topLeft.x + SCREEN_WIDTH + borderWidth, topLeft.y);
        path.lineTo(topLeft.x + SCREEN_WIDTH + borderWidth, topLeft.y + SCREEN_WIDTH + borderWidth);
        path.lineTo(topLeft.x, topLeft.y + SCREEN_WIDTH + borderWidth);
        path.closePath();
        canvasCtx.lineWidth = borderWidth;
        canvasCtx.strokeStyle = '#726d72';
        canvasCtx.stroke(path);
    }

    function drawTopWidgetLine() {
        const borderWidth = 1;
        const offset = SCREEN_WIDTH / 2;
        const topLeft = {
            x: uiEditorElement.clientWidth / 2 - offset - borderWidth / 2,
            y: uiEditorElement.clientHeight / 2 - offset - borderWidth / 2,
        }
        const path = new Path2D();
        path.moveTo(topLeft.x, topLeft.y + 24);
        path.lineTo(topLeft.x + SCREEN_WIDTH + borderWidth, topLeft.y + 24);
        path.closePath();
        canvasCtx.lineWidth = borderWidth;
        canvasCtx.strokeStyle = '#726d72';
        canvasCtx.stroke(path);
    }

    function copyActiveElements() {
        copiedUiElements = selectedUiElements;
    }

    function pasteCopiedElements(x, y) {
        const groupedUIElements = {}
        const currentGroupNames = new Set<string>();
        guiCreatorDataStore.getAllElementsAsList().forEach((uiElement) => {
            if (uiElement.group) {
                currentGroupNames.add(uiElement.group);
            }
        });

        let topLeftCorner;
        const newElements = copiedUiElements
            .map(uiElementOf)
            .map((uiElement) => {
                if (!topLeftCorner) {
                    topLeftCorner = {
                        x: uiElement.x,
                        y: uiElement.y,
                    }
                } else {
                    topLeftCorner.x = uiElement.x < topLeftCorner ? uiElement.x : topLeftCorner.x;
                    topLeftCorner.y = uiElement.y < topLeftCorner ? uiElement.y : topLeftCorner.y;
                }
                return uiElement;
            })
            .map(uiElement => {
                if (!uiElement.group) return uiElement;
                if (groupedUIElements[uiElement.group]) {
                    uiElement.group = groupedUIElements[uiElement.group];
                    return uiElement;
                } else {
                    let count = 1;
                    let newGroupName = `${uiElement.group} Copy`;
                    while (currentGroupNames.has(newGroupName)) {
                        newGroupName = `${uiElement.group} Copy ${count}`;
                        count++;
                    }
                    groupedUIElements[uiElement.group] = newGroupName;
                    currentGroupNames.add(newGroupName);
                    uiElement.group = newGroupName;
                    return uiElement;
                }
            })
            .map((element) => {
                element.id = genId();
                element.name = genId();
                element.x = x + element.x - topLeftCorner.x;
                element.y = y + element.y - topLeftCorner.y;
                return element;
            });
        newElements.forEach(guiCreatorDataStore.add);

    }

    function getMousePosition() {
        return {
            x: pointerPosition.x - origin.x,
            y: pointerPosition.y - origin.y,
        }
    }

    function loadCachedData() {
        loadConfig();
    }

    function addRect(x: number, y: number) {
        guiCreatorDataStore.add(new UIRectangle({
                x: x - 50,
                y: y - 50,
                name: Date.now().toString(),
                width: 100,
                height: 100,
                color: '#000000',
                z: getNewZValue(),
            }),
        );
    }

    function addButton(x: number, y: number) {
        guiCreatorDataStore.add(new UIButton({
                x: x - 50,
                y: y - 50,
                name: Date.now().toString(),
                width: 100,
                height: 100,
                backgroundColor: '#9bc09b',
                textColor: '#000000',
                text: 'test',
                fontSize: 20,
                showHitBox: false,
                hitBoxOffsetXLeft: 0,
                hitBoxOffsetXRight: 0,
                hitBoxOffsetYTop: 0,
                hitBoxOffsetYBottom: 0,
                vibrate: true,
                z: getNewZValue(),
            }),
        );
    }

    function addTriangle(x: number, y: number) {
        guiCreatorDataStore.add(new UITriangle({
                x: x - 50,
                y: y + 50,
                name: Date.now().toString(),
                width: 100,
                height: 100,
                color: '#000000',
                rotation: 0,
                clickable: false,
                hitBoxType: HitBoxTypes.RECTANGLE,
                points: [],
                displayHitBox: false,
                hitBoxOffsetXLeft: 0,
                hitBoxOffsetXRight: 0,
                hitBoxOffsetYTop: 0,
                hitBoxOffsetYBottom: 0,
                vibrate: true,
                z: getNewZValue(),
            }),
        );
    }

    function addText(x: number, y: number) {
        guiCreatorDataStore.add(new UIText({
                x: x,
                y: y,
                width: 100,
                name: Date.now().toString(),
                text: 'this is a default test text to test the length ',
                fontSize: 20,
                fitText: false,
                textAlignment: TextAlignmentTypes.LEFT,
                z: getNewZValue(),
            }),
        );
    }

    function addSlider(x: number, y: number) {
        guiCreatorDataStore.add(new UISlider({
            x: x,
            y: y,
            length: 100,
            name: Date.now().toString(),
            vertical: false,
            z: getNewZValue(),
            sliderPosition: 0.5,
            sliderSize: 5,
            sliderHitBoxOffset: 0,
            sliderShowHitBox: false,
        }));
    }

    function removeObjects() {
        const idsToRemove = selectedUiElements.map((uiElement) => uiElement.id);
        guiCreatorDataStore.removeByIds(idsToRemove);
        elementSelectionStore.set([]);
    }

    function onMouseMove(e: MouseEvent) {
        const cRect = canvasElement.getBoundingClientRect();
        const newMousePositionX = Math.round(e.clientX - cRect.left);
        const newMousePositionY = Math.round(e.clientY - cRect.top);
        const delta = {
            x: newMousePositionX - pointerPosition.x,
            y: newMousePositionY - pointerPosition.y,
        }
        pointerPosition = {
            x: newMousePositionX,
            y: newMousePositionY,
        }
        if (e.buttons === 1) {
            let newSelectedUIElements = selectedUiElements;
            if (!mouseDragging) {

                const newSelectedUIElement = uiElementsList.filter(uiElement => uiElement.isSelected(newMousePositionX - origin.x, newMousePositionY - origin.y)).reduce((highestUiElement, uiElement) => {
                    if (!highestUiElement) return uiElement;
                    return uiElement.z > highestUiElement.z ? uiElement : highestUiElement;
                }, null);
                newSelectedUIElements = newSelectedUIElement ? [newSelectedUIElement] : [];
                const groupUIElements = getOtherGroupUIElements(newSelectedUIElements);
                const selectedElementWithMouseOverIt = newSelectedUIElements.find((uiElement) => elementSelectionStore.isSelected(uiElement));
                if (selectedElementWithMouseOverIt) {
                    selectedUiElements.forEach(uiElement => newSelectedUIElements.push(uiElement))
                }
                groupUIElements.forEach(uiElement => newSelectedUIElements.push(uiElement));
                elementSelectionStore.set(newSelectedUIElements);
            }
            mouseDragging = true;
            const updatedSelected = newSelectedUIElements.map((selectedUiElement) => {
                selectedUiElement.moveElement(delta.x, delta.y);

                return selectedUiElement;
            })
            if (updatedSelected.length > 0) {
                guiCreatorDataStore.updateMultiple(updatedSelected, currentPage);
            }
        }
    }

    function onMouseUp(e: MouseEvent) {
        const pointerPos = getMousePosition();
        if (!mouseDragging) {
            const currentSelectedUIElements = e.ctrlKey ? selectedUiElements : [];
            let newSelectedUIElements = [];
            let newSelectedUIElement = uiElementsList.filter((uiElement) => uiElement.isSelected(pointerPos.x, pointerPos.y)).reduce((highestUiElement, uiElement) => {
                if (!highestUiElement) return uiElement;
                return uiElement.z > highestUiElement.z ? uiElement : highestUiElement;
            }, null);
            if (newSelectedUIElement) {
                newSelectedUIElements.push(newSelectedUIElement);
            }
            newSelectedUIElements = [...currentSelectedUIElements, ...newSelectedUIElements];
            const groupUIElements = getOtherGroupUIElements(newSelectedUIElements);
            groupUIElements.forEach(uiElement => newSelectedUIElements.push(uiElement));
            elementSelectionStore.set(newSelectedUIElements);
        } else {
            mouseDragging = false;
        }
    }

    function getOtherGroupUIElements(selectedUiElements) {
        const otherGroupUiElements = [];
        const selectedUiElementIds = new Set<string>();
        const selectedGroups = selectedUiElements.map((uiElement) => {
            selectedUiElementIds.add(uiElement.id);
            return uiElement;
        })
            .filter((uiElement) => uiElement.group)
            .reduce((groupSet, uiElement) => {
                if (decoupledGroupsStore.isDecoupled(uiElement.group)) return groupSet;
                groupSet.add(uiElement.group);
                return groupSet;
            }, new Set<string>());
        uiElementsList.forEach(uiElement => {
            if (selectedGroups.has(uiElement.group) && !selectedUiElementIds.has(uiElement.id)) {
                otherGroupUiElements.push(uiElement);
            }
        });
        return otherGroupUiElements;
    }

    function keyPress(e) {
        if (isUiInputFocused) return;
        const mousePosition = getMousePosition();
        if (e.key === 'n') {
            addRect(mousePosition.x, mousePosition.y);
        }
        if (e.key === 'd') {
            removeObjects();
        }
        if (e.key === 't') {
            addText(mousePosition.x, mousePosition.y);
        }
        if (e.key === 'b') {
            addButton(mousePosition.x, mousePosition.y);
        }
        if (e.key === 'f') {
            addTriangle(mousePosition.x, mousePosition.y);
        }
        if (e.key === 'c') {
            copyActiveElements();
        }
        if (e.key === 'v') {
            pasteCopiedElements(mousePosition.x, mousePosition.y);
        }
        if (e.key === 'g') {
            groupSelectedElements();
        }
        if (e.key === 's') {
            addSlider(mousePosition.x, mousePosition.y);
        }
    }

    function groupSelectedElements() {
        const currentGroups: Set<string> = guiCreatorDataStore.getAllElementsAsList().reduce((groupSet: Set<string>, uiElement: UIElement) => {
            if (uiElement.group) {
                groupSet.add(uiElement.group);
            }
            return groupSet;
        }, new Set<string>());
        let groupCounter = currentGroups.size;
        let newGroupName = `Group ${groupCounter}`;
        while (currentGroups.has(newGroupName)) {
            groupCounter++;
            newGroupName = `Group ${groupCounter}`;
        }
        const uiElements = uiElementsList.filter((uiElement) => elementSelectionStore.isSelected(uiElement)).map((uiElement) => {
            uiElement.group = newGroupName;
            return uiElement;
        });
        guiCreatorDataStore.updateMultiple(uiElements, currentPage);
    }

    function onKeyDown(e) {
        if (isUiInputFocused) return;
        let deltaX = 0;
        let deltaY = 0;
        let distance = e.shiftKey ? 10 : 1;
        if (e.keyCode === 37) {
            deltaX += -distance;
        }
        if (e.keyCode === 38) {
            deltaY += -distance;
        }
        if (e.keyCode === 39) {
            deltaX += distance;
        }
        if (e.keyCode === 40) {
            deltaY += distance;
        }
        if (deltaX !== 0 || deltaY !== 0) {
            moveSelectedElements(deltaX, deltaY);
        }
    }

    function moveSelectedElements(xDirection, yDirection) {
        const updatedElements = selectedUiElements.map(element => {
            element.x += xDirection;
            element.y += yDirection;
            return element;
        });
        if (updatedElements.length > 0) {
            guiCreatorDataStore.updateMultiple(updatedElements, currentPage);
        }
    }


    document.addEventListener("keypress", keyPress);
    document.addEventListener("keydown", onKeyDown);
</script>

<main>
    <h1>Bangle js GUI creator</h1>
    <div id="ui-editor" bind:this={uiEditorElement}>
        <UIEditorNavBar></UIEditorNavBar>
        <div class="border-solid border-b-accent border-gray-800"
             on:mouseup={onMouseUp}>
            <canvas width="600" height="600" on:mousemove="{onMouseMove}"
                    on:keypress={keyPress}
                    bind:this={canvasElement}></canvas>
        </div>
    </div>
    <div>
        <LeftSideBar class="left-side-bar"></LeftSideBar>
    </div>

    <div>
        <RightSideBar class="right-side-bar" selectedUiElements="{selectedUiElements}" origin="{origin}"
                      onShowWidgetBarTopChange="{onShowTopWidgetBarChange}"
                      showWidgetBarTop="{showTopWidgetBar}"></RightSideBar>
    </div>
</main>

<style>
    main {
        text-align: center;
        padding: 1em;
        height: 100%;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 20% 60% 20%;
        grid-template-rows: auto 80% 10%;
        background-color: hsl(var(--b2));
        grid-template-areas:
			'a a a'
			'b d c'
			'b d c';
    }

    .left-side-bar {
        grid-area: c;
    }

    .left-side-bar {
        grid-area: b;
    }

    #ui-editor {
        width: 100%;
        grid-area: d;
        height: 100%;
        margin: auto;
        background-color: hsl(var(--nc));
    }


    h1 {
        grid-area: a;
        text-transform: uppercase;
        font-size: 4em;
        font-weight: 100;
        grid-template-rows: 100px 100px 100px;
    }

    @media (min-width: 640px) {
        main {
            max-width: none;
        }
    }
</style>
